import React, { useEffect, useRef, useState } from 'react'
import axios from 'axios'
import { useRouter } from 'next/router'
import useProfile from 'common/providers/useProfile'

// Check a fresh profile before mounting children: persisted profile data and
// browser history are not evidence of access for the current session.
const AmmaAccessGuard: React.FC = ({ children }) => {
  const router = useRouter()
  const routerRef = useRef(router)
  routerRef.current = router
  const { profile } = useProfile()
  const organisation = profile?.organisations?.[0]
  const scope = JSON.stringify([profile?.id, organisation?.id, organisation?.ammaEnabled])
  const [access, setAccess] = useState<{ scope: string; organisationId: string } | null>(null)

  useEffect(() => {
    let generation = 0
    let disposed = false
    let loggedOut = false
    const verify = async () => {
      if (loggedOut) return
      const request = ++generation
      try {
        const session = await API.auth.Cognito.getSession()
        const { data } = await axios.get(`${Project.api}me/`, {
          headers: {
            Authorization: `Bearer ${session.getAccessToken().getJwtToken()}`,
          },
          params: { _: Date.now() },
        })
        if (disposed || request !== generation) return
        const organisation = data?.organisations?.[0]
        if (!organisation?.id || organisation.ammaEnabled !== true) {
          setAccess(null)
          void routerRef.current.replace('/')
          return
        }
        // AMMA forms use this value for their requests. Never retain the
        // organisation belonging to a previous login.
        localStorage.setItem('OrganisationID', organisation.id)
        setAccess({ scope, organisationId: organisation.id })
      } catch {
        if (!disposed && request === generation) {
          setAccess(null)
          void routerRef.current.replace('/')
        }
      }
    }
    const revoke = () => {
      ++generation
      setAccess(null)
    }
    const onStorage = (event: StorageEvent) => {
      if (!event.key || event.key === 'OrganisationID') {
        revoke()
        void verify()
      }
    }
    const onLogout = () => {
      loggedOut = true
      revoke()
    }
    void verify()
    window.addEventListener('pageshow', verify)
    window.addEventListener('focus', verify)
    window.addEventListener('pagehide', revoke)
    window.addEventListener('storage', onStorage)
    window.addEventListener('account-logout', onLogout)
    return () => {
      disposed = true
      window.removeEventListener('pageshow', verify)
      window.removeEventListener('focus', verify)
      window.removeEventListener('pagehide', revoke)
      window.removeEventListener('storage', onStorage)
      window.removeEventListener('account-logout', onLogout)
    }
  }, [scope])

  return access && access.scope === scope ? (
    <React.Fragment key={access.organisationId}>{children}</React.Fragment>
  ) : null
}

export default AmmaAccessGuard
